const template = require('./myAccount.template');
const changePasswordDialogTemplate = require('./changePasswordDialog/changePasswordDialog.template');
require('./myAccount.styles');
require('./changePasswordDialog/changePasswordDialog.styles');
import {
    PhoenixToggleService,
    LocalizedResourcesService
} from 'phoenix/services';
import { TrackingService } from 'sharebase/services/trackingService';
import { TimeZone } from 'sharebase/services/timeZones';
import { ITimeZoneService } from 'sharebase/services/timeZoneService';
import { ChangePasswordDialogController } from './changePasswordDialog/changePasswordDialog.component';
import {
    srvStatToClientStat,
    UserStat
} from './statsList/statsList.component';

// Add user stats object keys to this list if you don't want them displayed in the UI
const userStatsBlacklist = [
    'SecurityViolationsByUser'
];

// Add user stats object keys to this list if the value should be displayed as a file size
// i.e. displayed as 5.3 KB instead of 5300
const userStatIsFileSizeValue = [
    'LibraryDiskUsage'
];    

/**
 * @ngdoc directive
 * @name sharebase.myAccount.pxMyAccount
 * @module sharebase.myAccount
 * @restrict #
 * @description
 *
 * `pxMyAccount` enapsulates the entire account management page. It is intended to be
 * used as a routed component instead of used within other components.
 */
export function MyAccountDirective(): ng.IDirective {
    return {
        restrict: 'E',
        controller: MyAccountController,
        controllerAs: 'vm',
        template,
        bindToController: true,
        scope: { }
    };
}

class MyAccountController {
    public static $inject = [
        '$q',
        '$mdToast',
        '$mdDialog',
        '$mdMedia',
        'pxAuthService',
        'pxPhoenixToggleService',
        'pxLocalizedResourcesService',
        'sbTrackingService',
        'sbTimeZoneService',
        'settingsService',
        'gettextCatalog'
    ];

    // holds the entire collection of user stats
    private userStats: UserStat[];
    // the first half of the user stats
    public userStatsLeftCol: UserStat[];
    // the second half of user stats.
    public userStatsRightCol: UserStat[];

    public notificationSettingsOn: boolean = false;
    public emailDigestEnabled: boolean = false;

    private timeZone: TimeZone;
    private timeZoneIsRequired: boolean = false;
    public usageLink = this.pxLocalizedResourcesService.termsOfService;

    constructor(
        private readonly $q:ng.IQService,
        private readonly $mdToast: any, 
        private readonly $mdDialog: any,
        private readonly $mdMedia: any,
        private readonly pxAuthService: any,
        private readonly pxPhoenixToggleService: PhoenixToggleService,
        private readonly pxLocalizedResourcesService: LocalizedResourcesService,
        private readonly sbTrackingService: TrackingService,
        private readonly sbTimeZoneService: ITimeZoneService,
        private readonly settingsService: any,
        private readonly gettextCatalog: any
    ) {

        // initialize stat properties to empty array before attempting to
        // load stats from server.
        this.setUserStats();
        this.pxAuthService.getUserStatistics()
            .then(data => {
                // server returns an object with keys representing the stats
                // and the value is an object with a Label and Count property
                // Convert this object to just an array of objects with mirrored
                // properties, except they are now camel cased. This data flow will
                // also remove any objects whose key is specified in the blacklist
                // keys array and add a property to objects signifying to use the
                // itemSizeFilter whose key is in the userStatIsFileSizeValue array.
                let stats = Object.keys(data)
                    .filter(key => userStatsBlacklist.indexOf(key) < 0)
                    .map(key => {
                        let stat = srvStatToClientStat(data[key]);

                        if (userStatIsFileSizeValue.indexOf(key) >= 0) {
                            stat.useItemSize = true;
                        }
                        return stat;
                    });
                this.setUserStats(stats);
            });

        this.pxPhoenixToggleService.with('notification-settings')
            .on(() => {
                this.notificationSettingsOn = true;
            });
        this.pxPhoenixToggleService.with('Core/EmailDigest')
            .on(() => {
                this.emailDigestEnabled = true;
                this.sbTimeZoneService.getUserTimeZone()
                    .then(tz => {
                        if (tz != null) {
                            this.timeZoneIsRequired = true;
                            return tz;
                        } else {
                            return this.sbTimeZoneService.getDeploymentTimeZone();
                        }
                    })
                    .then(tz => this.timeZone = tz);
            }); 
    }

    /**
     * Invokes the change password dialog, then displays a success message
     * after the dialog has closed.
     */
    public showChangePassword($event): void {
        let parentEl = angular.element(document.body);
        this.$mdDialog.show({
            parent: parentEl,
            targetEvent: $event,
            template: changePasswordDialogTemplate,
            controller: ChangePasswordDialogController,
            controllerAs: 'vm'
        })
        .then(result => {
            // result will only be truthy if the user successfully changed
            // their password, if it is falsy, they cancelled the form and
            // we don't need to do anything
            if (result) {
                let message = this.gettextCatalog.getString('Password successfully changed.');
                this.showMessage(message);
            }
        });
    }

    public onSettingsSave(): void {
        let message = this.gettextCatalog.getString('Your subscription settings have been saved');
        this.showMessage(message);
    }

    public onSettingsError(error: any): void {
        let message = this.gettextCatalog.getString('An error occurred while trying to save your settings');
        this.showMessage(message);
    }

    public onSettingsLoadError(error: any): void {
        let message = this.gettextCatalog.getString('An error occured while trying to load your subscription settings');
        this.showMessage(message);
    }

    public saveTimeZone() {
        if (this.timeZone) {
            return this.sbTimeZoneService.setUserTimeZone(this.timeZone)
                .then(() => {
                    this.showMessage(this.gettextCatalog.getString('Your time zone has been saved'));
                    this.timeZoneIsRequired = true;
                })
                .catch(() => {
                    this.showMessage(this.gettextCatalog.getString('An unexpected error occurred while trying to save your time zone'));
                });
        }
        else {
            return this.$q.resolve();
        }
    }

    /**
     * Sets the local user stats fields by sorting the array,
     * then splitting it in half and saving each half to the userStats col fields.
     */
    private setUserStats(stats: UserStat[] = []): void {
        this.userStats = stats;
        this.userStats.sort((a, b) => a.label < b.label ? -1 : (a.label === b.label ? 0 : 1));
        let len = this.userStats.length;
        if (len > 0) {
            let half = Math.ceil(len / 2);
            this.userStatsLeftCol = this.userStats.slice(0, half);
            this.userStatsRightCol = this.userStats.slice(half);
        } else {
            this.userStatsLeftCol = [];
            this.userStatsRightCol = [];
        }
    }

    private showMessage(message: string) {
        this.$mdToast.show(
            this.$mdToast.simple()
                .textContent(message)
                .position('bottom right')
                .hideDelay(3000)
        );
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/components/myAccount/myAccount.component.ts